"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateLiquidityShock = exports.calculateLiqTable = exports.calculateRollRiskAndLiquidityShockTable = exports.calculateCurrencyDeltaTables = void 0;
const calculationsCore_1 = require("../calculationsCore");
const pnlCalculations_1 = require("../pnlCalculations");
const types_1 = require("../types");
const utils_1 = require("./utils");
const futurePredicate = (position) => (0, pnlCalculations_1.isFutureLikeInstrument)(position.instrument);
const optionsShortPredicate = (position) => position.amount < 0 && (0, pnlCalculations_1.isOptionLikeInstrument)(position.instrument);
const optionsLongPredicate = (position) => position.amount > 0 && (0, pnlCalculations_1.isOptionLikeInstrument)(position.instrument);
const futuresAndShortsPredicate = (position) => futurePredicate(position) || optionsShortPredicate(position);
const calculateDeltaTable = (expiryMap, posGreeksMap) => {
    let table = [];
    const emptyRow = {
        expiry: "",
        futures: 0,
        optionsShort: 0,
        optionsLong: 0,
        futuresAndShorts: 0,
        totalDelta: 0,
    };
    for (const expiry of Object.keys(expiryMap)) {
        const positions = expiryMap[expiry];
        const row = positions.reduce((memo, position) => {
            const sum = Object.assign({}, memo);
            const currentDelta = posGreeksMap[position.uuid].delta;
            sum.totalDelta += currentDelta;
            sum.futures = futurePredicate(position)
                ? sum.futures + currentDelta
                : sum.futures;
            sum.optionsShort = optionsShortPredicate(position)
                ? sum.optionsShort + currentDelta
                : sum.optionsShort;
            sum.optionsLong = optionsLongPredicate(position)
                ? sum.optionsLong + currentDelta
                : sum.optionsLong;
            sum.futuresAndShorts = futuresAndShortsPredicate(position)
                ? sum.futuresAndShorts + currentDelta
                : sum.futuresAndShorts;
            return sum;
        }, Object.assign(Object.assign({}, emptyRow), { expiry }));
        table.push(row);
    }
    const totalRow = table.reduce((memo, current) => {
        return {
            expiry: "total",
            futures: memo.futures + current.futures,
            futuresAndShorts: memo.futuresAndShorts + current.futuresAndShorts,
            optionsLong: memo.optionsLong + current.optionsLong,
            optionsShort: memo.optionsShort + current.optionsShort,
            totalDelta: memo.totalDelta + current.totalDelta,
        };
    }, Object.assign({}, emptyRow));
    const total = totalRow.futuresAndShorts < 0
        ? Math.min(0, Math.max(totalRow.optionsLong + totalRow.futuresAndShorts, totalRow.futuresAndShorts))
        : Math.max(0, Math.min(totalRow.optionsLong + totalRow.futuresAndShorts, totalRow.futuresAndShorts));
    return { total, table, totalRow };
};
const calculateCurrencyDeltaTables = (groupedPositionsByCurrencyExpiry, posGreeksMap) => {
    const res = {};
    for (const currency of Object.keys(groupedPositionsByCurrencyExpiry)) {
        res[currency] = calculateDeltaTable(groupedPositionsByCurrencyExpiry[currency], posGreeksMap);
    }
    return res;
};
exports.calculateCurrencyDeltaTables = calculateCurrencyDeltaTables;
const calculateBasisRisk = (pmParams, T) => Math.max(pmParams.general.rollRisk.minAnnualisedPercentMove, Math.exp(pmParams.general.rollRisk.annualisedPercentMoveRisk * T) - 1);
const calculateRollRiskAndLiquidityShockTable = (currencyExpiryMap, posGreeksMap, pmParams, calculationTimestamp) => {
    const rollRiskAndLiquidityShockTable = {};
    for (const currency of Object.keys(currencyExpiryMap)) {
        rollRiskAndLiquidityShockTable[currency] = {};
        for (const expiry of Object.keys(currencyExpiryMap[currency])) {
            const positions = currencyExpiryMap[currency][expiry];
            const liquidityShock = positions.reduce((memo, current) => {
                return memo + posGreeksMap[current.uuid].delta;
            }, 0);
            const T = (0, calculationsCore_1.getPositionTimeToExpiryFromInstrumentString)(`xxx-${expiry}`, calculationTimestamp);
            const basisRisk = expiry.includes("PERP")
                ? pmParams.general.rollRisk.minAnnualisedPercentMove
                : calculateBasisRisk(pmParams, T);
            const rollRisk = basisRisk * liquidityShock;
            rollRiskAndLiquidityShockTable[currency][expiry] = {
                liquidityShock,
                rollRisk,
            };
        }
    }
    return rollRiskAndLiquidityShockTable;
};
exports.calculateRollRiskAndLiquidityShockTable = calculateRollRiskAndLiquidityShockTable;
const getIndexPriceForCurrencyFromTickers = (tickers, currency) => {
    const currencyInstrument = Object.keys(tickers).find((instrument) => (0, calculationsCore_1.getBaseCurrencyFromInstrumentString)(instrument) === currency);
    return tickers[currencyInstrument].indexPrice;
};
const calculateLiqTable = (rollRiskAndLiquidityShockTable, deltaTables, tickers, pmParams) => {
    const table = [];
    let usdRollRiskTotal = 0;
    let usdLiqShockTotal = 0;
    let usdMarginTotal = 0;
    for (const currency of Object.keys(rollRiskAndLiquidityShockTable)) {
        const indexPrice = getIndexPriceForCurrencyFromTickers(tickers, currency);
        const sumCurrencyLiqShockAbsolute = Object.keys(rollRiskAndLiquidityShockTable[currency]).reduce((memo, expiry) => {
            return (memo +
                Math.abs(rollRiskAndLiquidityShockTable[currency][expiry].liquidityShock));
        }, 0);
        const sumCurrencyRollRisk = Object.keys(rollRiskAndLiquidityShockTable[currency]).reduce((memo, expiry) => {
            return memo + rollRiskAndLiquidityShockTable[currency][expiry].rollRisk;
        }, 0);
        const rollRisk = Math.max(sumCurrencyLiqShockAbsolute *
            pmParams.general.rollRisk.minAnnualisedPercentMove, Math.abs(sumCurrencyRollRisk));
        const currencyDeltaTableTotalAbsolute = Math.abs(deltaTables[currency].total);
        const liqShock = currencyDeltaTableTotalAbsolute <
            pmParams.currencyParams[currency].liquidityShock.coinLimit
            ? 0
            : Math.min((currencyDeltaTableTotalAbsolute -
                pmParams.currencyParams[currency].liquidityShock.coinLimit) *
                pmParams.currencyParams[currency].liquidityShock.factor *
                currencyDeltaTableTotalAbsolute, currencyDeltaTableTotalAbsolute * 0.1);
        const usdRollRisk = indexPrice * rollRisk;
        const usdLiqShock = indexPrice * liqShock;
        const totalUsdMargin = usdLiqShock + usdRollRisk;
        usdRollRiskTotal += usdRollRisk;
        usdLiqShockTotal += usdLiqShock;
        usdMarginTotal += totalUsdMargin;
        table.push({
            currency: currency,
            factor: pmParams.currencyParams[currency].liquidityShock.factor,
            indexPrice,
            liqShock,
            rollRisk,
            total: liqShock + rollRisk,
            usdLiqShock: usdLiqShock,
            usdMargin: totalUsdMargin,
            usdRollRisk: usdRollRisk,
            coinLimit: pmParams.currencyParams[currency].liquidityShock.coinLimit,
        });
    }
    return { table, usdRollRiskTotal, usdLiqShockTotal, usdMarginTotal };
};
exports.calculateLiqTable = calculateLiqTable;
const calculateLiquidityShock = (positions, posGreeksMap, tickers, pmParams, calculationTimestamp) => {
    const notDvolPositions = positions.filter((p) => (0, calculationsCore_1.getInstrumentType)(p.instrument) !== types_1.PositionType.VolatilityFuture);
    const groupedPositionsByCurrencyExpiry = (0, utils_1.groupPositionsByCurrencyAndExpiry)(notDvolPositions);
    const deltaTables = (0, exports.calculateCurrencyDeltaTables)(groupedPositionsByCurrencyExpiry, posGreeksMap);
    const rollRiskAndLiqShockTable = (0, exports.calculateRollRiskAndLiquidityShockTable)(groupedPositionsByCurrencyExpiry, posGreeksMap, pmParams, calculationTimestamp);
    const liqTable = (0, exports.calculateLiqTable)(rollRiskAndLiqShockTable, deltaTables, tickers, pmParams);
    return { liqTable, rollRiskAndLiqShockTable, deltaTables };
};
exports.calculateLiquidityShock = calculateLiquidityShock;
